/**
 * section-faq.js (AAV)
 * - Master + payload merge
 * - Tabs, accordions, sidebar nav, copy-link
 * - Search (OPTION B): searches across ALL tabs (not only current tab)
 * - Sidebar behavior:
 *    - Hidden while search field has any text (CSS uses .is-searching)
 *    - Sidebar returns only when search is cleared (empty field / ×)
 */

(function () {
  const ROOT_SEL = "[data-aav-faq]";
  const TAB_BTN_SEL = ".aav-tab";
  const TOGGLE_SEL = "[data-aav-toggle]";
  const ITEMS_SEL = "[data-aav-item]";
  const PANELS_SEL = "[data-tab-panel]";
  const NAV_UL_SEL = "[data-aav-nav]";
  const NAV_EMPTY_SEL = "[data-aav-nav-empty]";
  const SEARCH_SEL = "[data-aav-search]";
  const CLEAR_SEL = "[data-aav-clear]";

  // Payload wrapper rendered by additional FAQ sections (is_master=false)
  const PART_SEL = "[data-aav-faq-part]";

  function escId(id) {
    try {
      return CSS.escape(id);
    } catch (e) {
      return String(id || "").replace(/[^a-zA-Z0-9_-]/g, "\\$&");
    }
  }

  function setSearching(root, isSearching) {
    root.classList.toggle("is-searching", !!isSearching);
  }

  function getActiveTab(root) {
    return (
      root.querySelector(`${TAB_BTN_SEL}.is-active`)?.getAttribute("data-tab") ||
      root.getAttribute("data-default-tab") ||
      "home_page"
    );
  }

  function closeAll(root) {
    root.querySelectorAll(".aav-card.is-open").forEach((card) => {
      card.classList.remove("is-open");
      const btn = card.querySelector(TOGGLE_SEL);
      if (btn) btn.setAttribute("aria-expanded", "false");
    });
  }

  function openCard(card) {
    card.classList.add("is-open");
    const btn = card.querySelector(TOGGLE_SEL);
    if (btn) btn.setAttribute("aria-expanded", "true");
  }

  function setActiveNav(root, navId) {
    const nav = root.querySelector(NAV_UL_SEL);
    if (!nav) return;
    nav.querySelectorAll("a[data-nav]").forEach((a) => {
      a.classList.toggle("is-active", a.getAttribute("data-nav") === navId);
    });
  }

  function rebuildNav(root, tab) {
    const nav = root.querySelector(NAV_UL_SEL);
    const empty = root.querySelector(NAV_EMPTY_SEL);
    if (!nav) return;

    let visibleCount = 0;
    nav.querySelectorAll("li[data-nav-tab]").forEach((li) => {
      const isVisible = li.getAttribute("data-nav-tab") === tab;
      li.style.display = isVisible ? "" : "none";
      if (isVisible) visibleCount++;
    });

    if (empty) empty.hidden = visibleCount > 0;
  }

  function setActiveTab(root, tab) {
    root.querySelectorAll(TAB_BTN_SEL).forEach((btn) => {
      const isOn = btn.getAttribute("data-tab") === tab;
      btn.classList.toggle("is-active", isOn);
      btn.setAttribute("aria-selected", isOn ? "true" : "false");
    });

    root.querySelectorAll(PANELS_SEL).forEach((el) => {
      el.classList.toggle("is-active", el.getAttribute("data-tab-panel") === tab);
    });

    rebuildNav(root, tab);
  }

  /**
   * OPTION B SEARCH:
   * - If query non-empty: show matching questions across ALL tabs
   * - Hide all intro blocks during search
   * - Keep sidebar hidden as long as input is non-empty (via .is-searching)
   * - When query becomes empty: restore previous tab fully
   */
  function applySearchAll(root, query) {
    const q = (query || "").trim().toLowerCase();
    const isSearchingNow = q.length > 0;

    if (isSearchingNow && !root.dataset.aavPrevTab) {
      root.dataset.aavPrevTab = getActiveTab(root);
    }

    setSearching(root, isSearchingNow);

    root.querySelectorAll(".aav-faq-tab[data-tab-panel]").forEach((sec) => {
      sec.style.display = isSearchingNow ? "none" : "";
    });

    root.querySelectorAll(ITEMS_SEL).forEach((it) => {
      const text = (it.getAttribute("data-q") || "").toLowerCase();
      const hit = !isSearchingNow || text.includes(q);

      it.style.display = hit ? "" : "none";
      if (hit) it.removeAttribute("data-hidden");
      else it.setAttribute("data-hidden", "true");

      // While searching across tabs, bypass tab visibility by forcing .is-active
      if (isSearchingNow) it.classList.add("is-active");
      else it.classList.remove("is-active");
    });

    closeAll(root);

    if (!isSearchingNow) {
      const prev = root.dataset.aavPrevTab || getActiveTab(root);
      delete root.dataset.aavPrevTab;

      root.querySelectorAll(ITEMS_SEL).forEach((it) => {
        it.style.display = "";
        it.removeAttribute("data-hidden");
      });

      setActiveTab(root, prev);
      setActiveNav(root, "");
    }
  }

  async function copyLink(selector) {
    const el = document.querySelector(selector);
    const id = el?.id;
    if (!id) return;

    const url = new URL(window.location.href);
    url.hash = id;

    try {
      await navigator.clipboard.writeText(url.toString());
    } catch (e) {
      const tmp = document.createElement("textarea");
      tmp.value = url.toString();
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
    }
  }

  function mergeParts(root) {
    const parts = Array.from(document.querySelectorAll(PART_SEL)).filter(
      (p) => !root.contains(p)
    );
    if (!parts.length) return;

    const masterNav = root.querySelector(NAV_UL_SEL);
    const masterPanels = root.querySelector("[data-aav-panels]");

    parts.forEach((part) => {
      const nav = part.querySelector(NAV_UL_SEL);
      if (masterNav && nav) {
        Array.from(nav.children).forEach((li) => masterNav.appendChild(li));
      }

      const panels = part.querySelector("[data-aav-panels]") || part.querySelector(".aav-right");
      if (masterPanels && panels) {
        Array.from(panels.children).forEach((node) => masterPanels.appendChild(node));
      }

      part.remove();
    });
  }

  function finishBoot(root) {
    requestAnimationFrame(() => {
      setTimeout(() => {
        root.classList.remove("aav-faq--boot");
      }, 120);
    });
  }

  function init(root) {
    mergeParts(root);

    const defaultTab = root.getAttribute("data-default-tab") || "home_page";
    setActiveTab(root, defaultTab);

    finishBoot(root);

    // open from hash if present
    if (location.hash) {
      const id = location.hash.slice(1);
      const target = root.querySelector(`#${escId(id)}`);
      if (target) {
        const tab = target.getAttribute("data-tab-panel") || defaultTab;
        setActiveTab(root, tab);

        if (id.startsWith("faq-") && target.classList.contains("aav-card")) {
          closeAll(root);
          openCard(target);
          history.replaceState(null, "", `#${id}`);
        }

        if (id.startsWith("sec-")) {
          setActiveNav(root, id);
          const y = target.getBoundingClientRect().top + window.pageYOffset - 90;
          window.scrollTo({ top: y, behavior: "auto" });
        }
      }
    }

    // tabs click
    root.addEventListener("click", (e) => {
      const btn = e.target.closest(TAB_BTN_SEL);
      if (!btn || !root.contains(btn)) return;

      e.preventDefault();
      const tab = btn.getAttribute("data-tab");
      if (!tab) return;

      const input = root.querySelector(SEARCH_SEL);
      if (input) input.value = "";

      delete root.dataset.aavPrevTab;
      setSearching(root, false);

      root.querySelectorAll(".aav-faq-tab[data-tab-panel]").forEach((sec) => {
        sec.style.display = "";
      });

      root.querySelectorAll(ITEMS_SEL).forEach((it) => {
        it.removeAttribute("data-hidden");
        it.style.display = "";
        it.classList.remove("is-active");
      });

      closeAll(root);
      setActiveTab(root, tab);
      setActiveNav(root, "");
      history.replaceState(null, "", "#");
    });

    // accordion toggle (does NOT exit search mode)
    root.addEventListener("click", (e) => {
      const t = e.target.closest(TOGGLE_SEL);
      if (!t || !root.contains(t)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      const card = t.closest(".aav-card");
      if (!card) return;

      const isOpen = card.classList.contains("is-open");
      closeAll(root);

      if (!isOpen) {
        openCard(card);
        history.replaceState(null, "", `#${card.id}`);
      } else {
        history.replaceState(null, "", "#");
      }
    });

    // sidebar nav click
    root.addEventListener("click", (e) => {
      const a = e.target.closest("a.js-aav-faq-nav");
      if (!a || !root.contains(a)) return;

      e.preventDefault();

      const navId = a.getAttribute("data-nav");
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("#")) return;

      const target = root.querySelector(href);
      if (!target) return;

      setActiveNav(root, navId);

      const y = target.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: "smooth" });

      history.replaceState(null, "", href);
    });

    // search (OPTION B)
    const search = root.querySelector(SEARCH_SEL);
    const clear = root.querySelector(CLEAR_SEL);

    if (search) {
      search.addEventListener("input", () => applySearchAll(root, search.value));

      if (clear) {
        clear.addEventListener("click", () => {
          search.value = "";
          applySearchAll(root, "");
          search.focus();
        });
      }
    }

    // copy link
    root.addEventListener("click", async (e) => {
      const link = e.target.closest("[data-aav-copy]");
      if (!link || !root.contains(link)) return;

      e.preventDefault();
      const sel = link.getAttribute("data-aav-copy");
      if (!sel) return;

      await copyLink(sel);

      const prev = link.textContent;
      link.textContent = "Copied ✓";
      setTimeout(() => (link.textContent = prev), 1200);
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    const root = document.querySelector(ROOT_SEL);
    if (!root) return;
    init(root);
  });
})();
